import { useCallback, useEffect, useRef } from 'react';
import {
  cancelWebSocketReconnect as cancelWsReconnect,
  cleanupCall as cleanupCallAction,
  cleanupSubscribers as cleanupSubscribersAction,
  cleanupWebSocket as cleanupWebSocketAction,
} from './janus/cleanup';
import { executeJoinRoom } from './janus/joinRoom';
import { createBindMicTrackLifecycle } from './janus/micLifecycle';
import {
  ensureCalleeCanHearPublisher,
  handlePublisherEvent as handlePublisherEventAction,
  rebuildPublisherPeerConnection as rebuildPublisherPc,
  sendPublisherJoin as sendPublisherJoinAction,
} from './janus/publisher';
import { recoverLocalAudioImpl } from './janus/recoverLocalAudio';
import { requestInboundMediaRefresh } from './janus/refreshInboundMedia';
import { refreshLocalStreamForReconnect } from './janus/refreshLocalStream';
import {
  reconnectWebSocketAndIce as reconnectWebSocketAndIceAction,
  scheduleWebSocketReconnect as scheduleWsReconnect,
} from './janus/reconnect';
import {
  attachWebSocketHandlers,
  routeTrickle as routeTrickleAction,
  type SignalingHandlerDeps,
} from './janus/signaling';
import {
  handleSubscriberEvent as handleSubscriberEventAction,
  subscribeToPublishers as subscribeToPublishersAction,
} from './janus/subscriber';
import type { JanusCallHook, JoinRoomParams, UseJanusCallOptions } from './janus/types';
import { txId } from './janus/utils';
import { useJanusSession } from './janus/useJanusSession';
import { useJanusWebEffects } from './janus/webEffects';

export type {
  JanusCallHook,
  JanusCallStatus,
  JoinRoomParams,
  UseJanusCallOptions,
} from './janus/types';

export function useJanusCall(options?: UseJanusCallOptions): JanusCallHook {
  const {
    r,
    getSession,
    callStatus,
    localStream,
    remoteStream,
    error,
    cleanupOnUnmount,
  } = useJanusSession(options);

  const sendJanus = useCallback((msg: Record<string, unknown>) => {
    const { r: refs } = getSession();
    if (refs.wsRef.current?.readyState === WebSocket.OPEN) {
      refs.wsRef.current.send(JSON.stringify(msg));
    }
  }, [getSession]);

  const sendToHandle = useCallback(
    (
      handleId: number,
      body: Record<string, unknown>,
      jsep?: RTCSessionDescriptionInit,
      transaction?: string
    ) => {
      const { r: refs } = getSession();
      if (!refs.sessionIdRef.current) return;
      sendJanus({
        janus: 'message',
        session_id: refs.sessionIdRef.current,
        handle_id: handleId,
        transaction: transaction || txId('vr'),
        body,
        ...(jsep ? { jsep } : {}),
      });
    },
    [getSession, sendJanus]
  );

  const sendPublisherMessage = useCallback(
    (body: Record<string, unknown>, jsep?: RTCSessionDescriptionInit, transaction?: string) => {
      const { r: refs } = getSession();
      if (!refs.handleIdRef.current) return;
      sendToHandle(refs.handleIdRef.current, body, jsep, transaction);
    },
    [getSession, sendToHandle]
  );

  const isActiveCallSession = useCallback(() => {
    const { r: refs } = getSession();
    return (
      refs.callStatusRef.current === 'oncall' ||
      refs.callStatusRef.current === 'joining' ||
      refs.isJoinedRef.current ||
      !!refs.publisherPcRef.current
    );
  }, [getSession]);

  const cleanupSubscribers = useCallback(() => {
    const { r: refs } = getSession();
    cleanupSubscribersAction(refs, sendJanus);
  }, [getSession, sendJanus]);

  const cancelWebSocketReconnect = useCallback(() => {
    cancelWsReconnect(getSession().r);
  }, [getSession]);

  const cleanupCall = useCallback(() => {
    const { r: refs, set: setters } = getSession();
    cleanupCallAction(refs, setters, sendJanus, cleanupSubscribers);
  }, [cleanupSubscribers, getSession, sendJanus]);

  const cleanupWebSocket = useCallback(() => {
    const { r: refs, set: setters } = getSession();
    cleanupWebSocketAction(refs, setters);
  }, [getSession]);

  const rejectPending = useCallback((message: string) => {
    const { r: refs } = getSession();
    refs.joinRoomRejectRef.current?.(new Error(message));
    refs.joinRoomRejectRef.current = null;
    refs.joinRoomResolveRef.current = null;
    refs.ensureSessionRejectRef.current?.(new Error(message));
    refs.ensureSessionRejectRef.current = null;
    refs.ensureSessionResolveRef.current = null;
  }, [getSession]);

  const subscribeToPublishers = useCallback(
    (publishers?: import('./janus/types').VideoRoomPublisher[]) => {
      subscribeToPublishersAction(getSession().r, sendJanus, publishers);
    },
    [getSession, sendJanus]
  );

  useEffect(() => {
    r.subscribeToPublishersRef.current = subscribeToPublishers;
  });

  const handleSubscriberEvent = useCallback(
    async (
      data: Record<string, unknown>,
      sub: import('./janus/types').SubscriberFeed,
      pluginBody: Record<string, unknown>
    ) => {
      const { r: refs, set: setters } = getSession();
      await handleSubscriberEventAction(refs, setters, data, sub, pluginBody, {
        sendJanus,
        sendToHandle,
        isActiveCallSession,
      });
    },
    [getSession, isActiveCallSession, sendJanus, sendToHandle]
  );

  const handlePublisherEvent = useCallback(
    async (data: Record<string, unknown>, pluginBody: Record<string, unknown>) => {
      const { r: refs, set: setters } = getSession();
      await handlePublisherEventAction(refs, setters, data, pluginBody, {
        sendJanus,
        sendPublisherMessage,
        subscribeToPublishersFn: (pubs) => refs.subscribeToPublishersRef.current(pubs),
        rejectPending,
        cleanupCall,
        cancelWebSocketReconnect,
      });
    },
    [cancelWebSocketReconnect, cleanupCall, getSession, rejectPending, sendJanus, sendPublisherMessage]
  );

  const handleJanusEvent = useCallback(
    async (data: Record<string, unknown>) => {
      const { r: refs } = getSession();
      const plugindata = data.plugindata as { data?: Record<string, unknown> } | undefined;
      const pluginBody = plugindata?.data;
      if (!pluginBody) return;

      const senderHandle = data.sender as number | undefined;

      if (senderHandle && senderHandle === refs.handleIdRef.current) {
        await handlePublisherEvent(data, pluginBody);
        return;
      }

      if (senderHandle) {
        const sub = refs.subscriberFeedsRef.current.find((s) => s.handleId === senderHandle);
        if (sub) {
          await handleSubscriberEvent(data, sub, pluginBody);
        }
      }
    },
    [getSession, handlePublisherEvent, handleSubscriberEvent]
  );

  const routeTrickle = useCallback(
    (data: Record<string, unknown>) => routeTrickleAction(getSession().r, data),
    [getSession]
  );

  const signalingDepsRef = useRef<SignalingHandlerDeps>({
    sendJanus,
    sendToHandle,
    handleJanusEvent,
    routeTrickle,
    cleanupCall,
    cleanupWebSocket,
    cancelWebSocketReconnect,
    rejectPending,
    isActiveCallSession,
  });
  signalingDepsRef.current = {
    sendJanus,
    sendToHandle,
    handleJanusEvent,
    routeTrickle,
    cleanupCall,
    cleanupWebSocket,
    cancelWebSocketReconnect,
    rejectPending,
    isActiveCallSession,
  };

  const attachWebSocketHandlersCb = useCallback(
    (ws: WebSocket) => {
      const { r: refs, set: setters } = getSession();
      attachWebSocketHandlers(ws, refs, setters, signalingDepsRef.current);
    },
    [getSession]
  );

  const ensureSession = useCallback(
    (wssUrl?: string): Promise<void> => {
      const { r: refs } = getSession();
      if (wssUrl) {
        refs.signalingUrlRef.current = wssUrl;
      }

      if (refs.isSessionReadyRef.current && refs.wsRef.current?.readyState === WebSocket.OPEN) {
        return Promise.resolve();
      }

      const existing = refs.wsRef.current;
      if (existing) {
        const rs = existing.readyState;
        if (rs === WebSocket.CONNECTING || rs === WebSocket.OPEN) {
          return new Promise((resolve, reject) => {
            refs.ensureSessionResolveRef.current = resolve;
            refs.ensureSessionRejectRef.current = reject;
          });
        }
        cleanupWebSocket();
      }

      return new Promise((resolve, reject) => {
        refs.ensureSessionResolveRef.current = resolve;
        refs.ensureSessionRejectRef.current = reject;

        const ws = new WebSocket(refs.signalingUrlRef.current, 'janus-protocol');
        refs.wsRef.current = ws;
        attachWebSocketHandlersCb(ws);
      });
    },
    [attachWebSocketHandlersCb, cleanupWebSocket, getSession]
  );

  const sendPublisherJoin = useCallback(
    (transaction: string, withSdpOffer: boolean) =>
      sendPublisherJoinAction(getSession().r, sendPublisherMessage, transaction, withSdpOffer),
    [getSession, sendPublisherMessage]
  );

  const rebuildPublisherPeerConnection = useCallback(() => {
    return rebuildPublisherPc(getSession().r, sendJanus, isActiveCallSession);
  }, [getSession, isActiveCallSession, sendJanus]);

  const scheduleWebSocketReconnect = useCallback(
    (immediate = false) => scheduleWsReconnect(getSession().r, isActiveCallSession, immediate),
    [getSession, isActiveCallSession]
  );

  const bindMicTrackLifecycle = useCallback(
    (track: MediaStreamTrack) => createBindMicTrackLifecycle(getSession().r)(track),
    [getSession]
  );

  const refreshLocalStreamForReconnectCb = useCallback(async () => {
    const { r: refs, set: setters } = getSession();
    return refreshLocalStreamForReconnect(refs, setters, bindMicTrackLifecycle);
  }, [bindMicTrackLifecycle, getSession]);

  const reconnectWebSocketAndIce = useCallback(async () => {
    const { set: setters } = getSession();
    await reconnectWebSocketAndIceAction(getSession().r, {
      cleanupSubscribers,
      rebuildPublisherPeerConnection,
      refreshLocalStreamForReconnect: refreshLocalStreamForReconnectCb,
      ensureSession,
      sendPublisherJoin,
      sendPublisherMessage,
      cancelWebSocketReconnect,
      scheduleWebSocketReconnect: () => scheduleWebSocketReconnect(),
      setRemoteStream: setters.setRemoteStream,
    });
  }, [
    cancelWebSocketReconnect,
    cleanupSubscribers,
    ensureSession,
    getSession,
    rebuildPublisherPeerConnection,
    refreshLocalStreamForReconnectCb,
    scheduleWebSocketReconnect,
    sendPublisherJoin,
    sendPublisherMessage,
  ]);

  const joinRoom = useCallback(
    (params: JoinRoomParams) =>
      executeJoinRoom(params, {
        r: getSession().r,
        set: getSession().set,
        messaging: { sendJanus, sendToHandle, sendPublisherMessage },
        callStatus,
        ensureSession,
        sendPublisherJoin,
        cleanupCall,
        bindMicTrackLifecycle,
        isActiveCallSession,
      }),
    [
      bindMicTrackLifecycle,
      callStatus,
      cleanupCall,
      ensureSession,
      getSession,
      isActiveCallSession,
      sendJanus,
      sendPublisherJoin,
      sendPublisherMessage,
      sendToHandle,
    ]
  );

  const recoverLocalAudio = useCallback(() => {
    const { r: refs, set: setters } = getSession();
    return recoverLocalAudioImpl(refs, setters, bindMicTrackLifecycle, sendPublisherMessage);
  }, [bindMicTrackLifecycle, getSession, sendPublisherMessage]);

  const refreshInboundMedia = useCallback(() => {
    const { r: refs } = getSession();
    requestInboundMediaRefresh(refs, sendPublisherMessage);
  }, [getSession, sendPublisherMessage]);

  useEffect(() => {
    r.scheduleWebSocketReconnectRef.current = scheduleWebSocketReconnect;
    r.reconnectWebSocketAndIceRef.current = reconnectWebSocketAndIce;
    r.recoverLocalAudioRef.current = recoverLocalAudio;
    r.refreshInboundMediaRef.current = refreshInboundMedia;
    r.publisherIceConnectedNotifyRef.current = () => {
      const { r: refs, set: setters } = getSession();
      void ensureCalleeCanHearPublisher(refs, setters, sendPublisherMessage, 'ice-connected');
    };
  });

  useJanusWebEffects({ r, isActiveCallSession, cancelWebSocketReconnect });

  const reconnectMedia = useCallback(async () => {
    await reconnectWebSocketAndIce();
  }, [reconnectWebSocketAndIce]);

  const setMuted = useCallback(
    (muted: boolean) => {
      const { r: refs } = getSession();
      refs.isMutedRef.current = muted;
      refs.localStreamRef.current?.getAudioTracks().forEach((t) => {
        t.enabled = !muted;
      });

      if (refs.isPublisherConfiguredRef.current && refs.handleIdRef.current) {
        sendPublisherMessage({
          request: 'configure',
          audio: true,
          video: false,
          muted,
        });
      }
    },
    [getSession, sendPublisherMessage]
  );

  const hangup = useCallback(() => {
    cancelWebSocketReconnect();
    const { r: refs } = getSession();
    if (refs.sessionIdRef.current && refs.handleIdRef.current && refs.isJoinedRef.current) {
      sendPublisherMessage({ request: 'leave' });
    }

    cleanupSubscribers();

    setTimeout(() => {
      cleanupCall();
      if (cleanupOnUnmount) {
        cleanupWebSocket();
      }
    }, 100);
  }, [
    cancelWebSocketReconnect,
    cleanupCall,
    cleanupOnUnmount,
    cleanupSubscribers,
    cleanupWebSocket,
    getSession,
    sendPublisherMessage,
  ]);

  
  useEffect(() => {
    if (!cleanupOnUnmount) return undefined;
    return () => {
      const { r: refs, set: setters } = getSession();
      cleanupSubscribersAction(refs, (msg) => {
        if (refs.wsRef.current?.readyState === WebSocket.OPEN) {
          refs.wsRef.current.send(JSON.stringify(msg));
        }
      });
      cleanupCallAction(refs, setters, sendJanus, () => cleanupSubscribersAction(refs, sendJanus));
      cleanupWebSocketAction(refs, setters);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanupOnUnmount]);

  return {
    callStatus,
    localStream,
    remoteStream,
    error,
    ensureSession,
    joinRoom,
    setMuted,
    hangup,
    recoverLocalAudio,
    reconnectMedia,
  };
}
